import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Loading from "../common/Loading";
import { ACCENT } from "../artistStudio/shared";
import FanMembershipShell from "./FanMembershipShell";
import { useErrorToast } from "../common/errorToast";
import { fanMembershipApi, recallArtist, isBillingPeriod, money, apiErrorMessage, formatDate } from "./fanMembershipApi";

// "Choose your plan": Basic and Premium, Monthly or Annual. Every price, label and note comes from the server.
// The chosen plan and billing period travel to checkout in the address (?plan=premium&period=annual), and switching
// the billing period only refreshes the prices: the plan the fan was looking at stays selected.

const Check = () => (
  <svg viewBox="0 0 20 20" className="w-5 h-5 shrink-0 mt-0.5" fill="none" aria-hidden="true">
    <circle cx="10" cy="10" r="10" fill={ACCENT} fillOpacity="0.12" />
    <path d="M6 10.5l2.7 2.7L14 7.8" stroke={ACCENT} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export function BillingToggle({ options, value, onChange, disabled }) {
  return (
    <div role="group" aria-label="Billing period" className="inline-flex rounded-full bg-gray-100 p-1">
      {options.map((o) => {
        const active = o.key === value;
        return (
          <button
            key={o.key}
            type="button"
            aria-pressed={active}
            disabled={disabled}
            onClick={() => !active && onChange(o.key)}
            className={`rounded-full px-4 md:px-6 py-2 text-sm font-semibold transition-colors whitespace-nowrap ${active ? "text-white shadow" : "text-gray-600 hover:text-gray-900"} disabled:cursor-wait`}
            style={active ? { backgroundColor: ACCENT } : undefined}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function PlanCard({ plan, period, selected, highlighted, onChoose, busy }) {
  const annual = period === "annual";
  // The server marks a plan the fan cannot buy now (the one they already have, or a lower one): the card is blurred and the button does nothing.
  const blocked = plan.blocked || null;
  return (
    <section
      aria-label={`${plan.name} plan`}
      aria-current={selected ? "true" : undefined}
      aria-disabled={blocked ? "true" : undefined}
      title={blocked ? blocked.message : undefined}
      className={`relative flex flex-col rounded-3xl bg-white p-6 md:p-8 ${highlighted ? "border-2 shadow-lg" : "border border-gray-200"} ${selected ? "ring-4 ring-green-100" : ""} ${blocked ? "opacity-60 blur-[1.5px] select-none" : ""}`}
      style={highlighted ? { borderColor: ACCENT } : undefined}
    >
      {plan.label && (
        <span className="absolute -top-3 left-6 rounded-full text-white text-xs font-semibold px-3 py-1" style={{ backgroundColor: ACCENT }}>
          {plan.label}
        </span>
      )}
      <h2 className="text-xl font-bold text-gray-900">{plan.name}</h2>
      <p className="mt-3 flex flex-wrap items-baseline gap-x-2">
        <span className="text-4xl font-bold text-gray-900">{money(plan.price, plan.currency)}</span>
        <span className="text-gray-500">{plan.price_unit}</span>
      </p>
      <div className="min-h-[2.75rem] mt-1 text-sm">
        {annual && plan.show_savings && (
          <p className="flex flex-wrap items-center gap-2">
            <span className="text-gray-400 line-through">{money(plan.annual_price_before_saving, plan.currency)}</span>
            <span className="rounded-full bg-green-50 font-semibold px-2 py-0.5" style={{ color: ACCENT }}>Save {Math.round(plan.savings_percent)}%</span>
          </p>
        )}
        {annual && <p className="text-gray-500">Charged once, upfront, for 12 months.</p>}
      </div>
      <ul className="mt-4 space-y-3 text-gray-700 flex-1">
        {plan.benefits.map((b) => (
          <li key={b} className="flex gap-3"><Check /><span>{b}</span></li>
        ))}
      </ul>
      <button
        type="button"
        onClick={() => { if (!blocked) onChoose(plan); }}
        disabled={busy || Boolean(blocked)}
        className={`mt-8 w-full rounded-full font-semibold py-3 transition-opacity disabled:opacity-60 disabled:cursor-not-allowed ${highlighted ? "text-white" : "bg-white border-2"}`}
        style={highlighted ? { backgroundColor: ACCENT } : { borderColor: ACCENT, color: ACCENT }}
      >
        {blocked ? (blocked.code === "ALREADY_SUBSCRIBED" ? "Your current plan" : "Not available now") : `Choose ${plan.name}`}
      </button>
    </section>
  );
}

export default function FanMembershipPlans() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const artistSlug = (params.get("artist") || "").toLowerCase() || recallArtist();
  const initialPeriod = isBillingPeriod(params.get("period")) ? params.get("period") : "monthly";
  const [period, setPeriod] = useState(initialPeriod);
  const [selected, setSelected] = useState(params.get("plan") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState("");
  useErrorToast(data ? error : ""); // a failed switch of period is a popup; with no plans loaded yet the retry panel shows it
  const latest = useRef(0);

  const sync = useCallback((nextPeriod, nextPlan) => {
    const next = {};
    if (artistSlug) next.artist = artistSlug;
    next.period = nextPeriod;
    if (nextPlan) next.plan = nextPlan;
    setParams(next, { replace: true });
  }, [artistSlug, setParams]);

  const load = useCallback(async (nextPeriod, { initial = false } = {}) => {
    const ticket = ++latest.current;
    if (initial) setLoading(true); else setSwitching(true);
    setError("");
    try {
      const result = await fanMembershipApi.plans(nextPeriod);
      if (ticket !== latest.current) return;
      setData(result);
      setPeriod(nextPeriod);
      sync(nextPeriod, selected);
    } catch (e) {
      if (ticket !== latest.current) return;
      setError(apiErrorMessage(e, "The plans could not be loaded. Please try again."));
    } finally {
      if (ticket === latest.current) { setLoading(false); setSwitching(false); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, sync]);

  useEffect(() => {
    load(initialPeriod, { initial: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const choose = (plan) => {
    setSelected(plan.code);
    // The price the fan is looking at travels along only so checkout can warn if it has changed; the server decides the price.
    const q = new URLSearchParams({ plan: plan.code, period, price: String(plan.price) });
    if (artistSlug) q.set("artist", artistSlug);
    navigate(`/fan-membership/checkout?${q.toString()}`);
  };

  const current = data?.current_subscription;
  let body;
  if (loading) {
    body = <div className="flex justify-center py-24" role="status" aria-label="Loading plans"><Loading /></div>;
  } else if (!data) {
    body = (
      <div className="text-center py-20 max-w-md mx-auto">
        <p className="text-gray-700 mb-4" role="alert">{error || "The plans could not be loaded."}</p>
        <button type="button" onClick={() => load(period, { initial: true })} className="rounded-full font-semibold text-white px-6 py-2.5" style={{ backgroundColor: ACCENT }}>Try again</button>
      </div>
    );
  } else {
    body = (
      <>
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900">{data.header}</h1>
          <p className="mt-3 text-gray-500">{data.supporting_text}</p>
          <div className="mt-6">
            <BillingToggle options={data.billing_periods} value={period} onChange={(p) => load(p)} disabled={switching} />
          </div>
          <p className="mt-3 text-sm text-gray-500" aria-live="polite">{data.period_note}</p>
        </div>

        {current && (
          <p className={`mt-6 text-center text-sm rounded-xl text-gray-800 max-w-2xl mx-auto px-4 py-3 ${current.status === "cancelled" ? "bg-amber-50" : "bg-green-50"}`} role="status">
            {current.status === "cancelled" ? (
              <>Your <strong>{current.plan_name}</strong> plan ({current.billing_period}) is <strong>cancelled</strong>. You keep your benefits until {formatDate(current.current_period_end)}.</>
            ) : (
              <>Your <strong>{current.plan_name}</strong> plan ({current.billing_period}) is <strong>active</strong> until {formatDate(current.current_period_end)}.</>
            )}{" "}
            <Link to="/fan-membership/details" className="font-semibold underline" style={{ color: ACCENT }}>View membership details</Link>
          </p>
        )}

        {data.plans.length === 0 && (
          <p className="mt-10 text-center text-gray-600" role="status">No membership plans are available right now. Please check back soon.</p>
        )}
        <div className={`mt-10 grid gap-6 md:grid-cols-2 max-w-4xl mx-auto transition-opacity ${switching ? "opacity-60" : ""}`}>
          {data.plans.map((plan) => (
            <PlanCard
              key={plan.id}
              plan={plan}
              period={period}
              selected={selected === plan.code}
              highlighted={Boolean(plan.label)}
              onChoose={choose}
              busy={switching}
            />
          ))}
        </div>
        {artistSlug && (
          <p className="mt-8 text-center">
            <Link to={`/artists/${encodeURIComponent(artistSlug)}`} className="text-sm font-semibold underline" style={{ color: ACCENT }}>Back to the artist page</Link>
          </p>
        )}
      </>
    );
  }

  return <FanMembershipShell>{body}</FanMembershipShell>;
}
